import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography, Paper } from '@mui/material';
import { ContentCopy, Refresh, Visibility, VisibilityOff, ShieldOutlined, ArrowBack } from '@mui/icons-material';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import OTPModal from '../../../components/OTPModal';
import NewSecretModal from '../../../components/NewSecretModal';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import {
  CREATE_WEBHOOK_CONFIG,
  READ_WEBHOOK_CONFIG,
  UPDATE_WEBHOOK_CONFIG,
} from '../../../constants/webhookPermissions';

import {
  useGenerateOTPHandler,
  useViewSecretHandler,
  useRegenerateSecretHandler,
} from '../dataHandlers/mutateWebhookSigningSecret';
import RegenerateSecretWarning from './RegenerateSecretWarning';

const ManageSigningSecretPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { webhookId } = useParams();
  const location = useLocation();
  const { hasWebhookPermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const canViewSecret = hasWebhookPermission(READ_WEBHOOK_CONFIG);
  const canRegenerateSecret =
    hasWebhookPermission(CREATE_WEBHOOK_CONFIG) || hasWebhookPermission(UPDATE_WEBHOOK_CONFIG);

  const webhookName = location.state?.webhookName;

  const [revealedSecret, setRevealedSecret] = useState('');
  const [newSecret, setNewSecret] = useState('');

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [newSecretModalOpen, setNewSecretModalOpen] = useState(false);

  const [otpAction, setOtpAction] = useState('');
  const [warningOpen, setWarningOpen] = useState(false);

  const [viewCooldown, setViewCooldown] = useState(0);
  const [regenerateCooldown, setRegenerateCooldown] = useState(0);
  const viewIntervalRef = useRef(null);
  const regenerateIntervalRef = useRef(null);

  const startTimer = (action) => {
    const isView = action === 'view_webhook_secret';
    const setCooldown = isView ? setViewCooldown : setRegenerateCooldown;
    const intervalRef = isView ? viewIntervalRef : regenerateIntervalRef;

    if (intervalRef.current) clearInterval(intervalRef.current);

    setCooldown(120);

    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (viewIntervalRef.current) clearInterval(viewIntervalRef.current);
      if (regenerateIntervalRef.current) clearInterval(regenerateIntervalRef.current);
    };
  }, []);

  const { onSubmit: generateOTP, isPending: isGeneratingOTP } = useGenerateOTPHandler({});

  const { onSubmit: viewSecret, isPending: isViewingSecret } = useViewSecretHandler({
    onSuccess: (data) => {
      setRevealedSecret(data.secret);
      setOtpModalOpen(false);
    },
  });

  const { onSubmit: regenerateSecret, isPending: isRegeneratingSecret } = useRegenerateSecretHandler({
    onSuccess: (data) => {
      setNewSecret(data.secret);
      setNewSecretModalOpen(true);
      setOtpModalOpen(false);
      setRevealedSecret('');
    },
  });

  const isLoading = isGeneratingOTP || isViewingSecret || isRegeneratingSecret;

  const handleViewSecret = () => {
    if (!canViewSecret) {
      return;
    }
    setOtpAction('view_webhook_secret');
    setOtpModalOpen(true);
  };

  const handleRegenerateSecret = () => {
    if (!canRegenerateSecret) {
      return;
    }
    setWarningOpen(true);
  };

  const handleProceedRegenerate = () => {
    setWarningOpen(false);

    setOtpAction('regenerate_webhook_secret');
    setOtpModalOpen(true);
  };

  const handleHideSecret = () => {
    setRevealedSecret('');
  };

  const handleOTPSubmit = (otp) => {
    if (otpAction === 'view_webhook_secret') {
      viewSecret({ id: webhookId, otp });
    }
    if (otpAction === 'regenerate_webhook_secret') {
      regenerateSecret({ id: webhookId, otp });
    }
  };

  const handleGenerateOTP = async (action) => {
    await generateOTP({ id: webhookId, action });
    startTimer(action);
  };

  const handleCloseNewSecretModal = () => {
    setNewSecretModalOpen(false);
    setNewSecret('');
    setRevealedSecret('');
  };

  const currentCooldown = otpAction === 'view_webhook_secret' ? viewCooldown : regenerateCooldown;

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);

      dispatch(
        showSnackbar({
          message: `${label} copied to clipboard`,
          severity: 'success',
        }),
      );
    } catch {
      dispatch(
        showSnackbar({
          message: 'Failed to copy',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1100 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={4}>
          <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }}>
            <ArrowBack />
          </IconButton>

          <ShieldOutlined sx={{ color: '#6366f1' }} />

          <Typography variant="h5" fontWeight={600}>
            Manage Signing Secret
          </Typography>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            width: '100%',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Webhook Configuration
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={4}>
            Manage your webhook signing secret for {webhookName || 'this webhook'}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 130 }}>
              Signing Secret
            </Typography>

            <TextField
              fullWidth
              value={revealedSecret || '••••••••••••••••••••••••••••••••'}
              type={revealedSecret ? 'text' : 'password'}
              InputProps={{ readOnly: true }}
              size="small"
            />

            {revealedSecret && (
              <IconButton onClick={() => copyToClipboard(revealedSecret, 'Signing Secret')}>
                <ContentCopy fontSize="small" />
              </IconButton>
            )}
          </Stack>

          <Stack direction="row" spacing={2} mt={4}>
            {revealedSecret ? (
              <Button variant="outlined" startIcon={<VisibilityOff />} onClick={handleHideSecret}>
                Hide Secret
              </Button>
            ) : (
              <PermissionDeniedWrapper show={!canViewSecret} title={accessDeniedMessage}>
                <Button
                  variant="contained"
                  startIcon={<Visibility />}
                  onClick={handleViewSecret}
                  disabled={!canViewSecret}
                  sx={{
                    background: '#6366f1',
                    textTransform: 'none',
                  }}
                >
                  View Secret
                </Button>
              </PermissionDeniedWrapper>
            )}

            <PermissionDeniedWrapper show={!canRegenerateSecret} title={accessDeniedMessage}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRegenerateSecret}
                disabled={!canRegenerateSecret}
                sx={{ textTransform: 'none' }}
              >
                Regenerate Secret
              </Button>
            </PermissionDeniedWrapper>
          </Stack>
        </Paper>

        <OTPModal
          open={otpModalOpen}
          onClose={() => {
            setOtpModalOpen(false);
            if (otpAction === 'view_secret') {
              setRevealedSecret('');
            }
          }}
          onSubmit={handleOTPSubmit}
          onGenerateOTP={handleGenerateOTP}
          action={otpAction}
          isLoading={isLoading}
          cooldown={currentCooldown}
        />

        <NewSecretModal open={newSecretModalOpen} onClose={handleCloseNewSecretModal} secret={newSecret} />
        <RegenerateSecretWarning
          open={warningOpen}
          onClose={() => setWarningOpen(false)}
          onProceed={handleProceedRegenerate}
        />
      </Box>
    </Box>
  );
};

export default ManageSigningSecretPage;
